import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useEffect, useState } from 'react';
import { http } from '../../api/http';

type Status = 'new' | 'answered' | 'archived';

interface ContactMessage {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
  status?: Status;
  createdAt: string;
}

export const ContactMessagesPage = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchMessages = async () => {
    try {
      setError(null);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await http.get<{ success: boolean; data: ContactMessage[] }>(
        '/contact-messages',
        { params }
      );
      setMessages(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load messages');
    }
  };

  useEffect(() => {
    void fetchMessages();
  }, [statusFilter]);

  const handleStatusChange = async (id: string, status: Status) => {
    try {
      await http.patch(`/contact-messages/${id}`, { status });
      setMessages((prev) =>
        prev.map((m) => (m._id === id ? { ...m, status } : m))
      );
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    try {
      await http.delete(`/contact-messages/${deleteId}`);
      setMessages((prev) => prev.filter((m) => m._id !== deleteId));
      setDeleteId(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Contact Messages
      </Typography>

      <Tabs
        value={statusFilter}
        onChange={(_, v) => setStatusFilter(v)}
        sx={{ mb: 2 }}
      >
        <Tab label="All" value="all" />
        <Tab label="New" value="new" />
        <Tab label="Answered" value="answered" />
        <Tab label="Archived" value="archived" />
      </Tabs>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((m) => (
                <TableRow key={m._id} hover>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.email}</TableCell>
                  <TableCell>{m.phone || '—'}</TableCell>
                  <TableCell>{m.subject || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>{m.message}</TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={m.status ?? 'new'}
                        onChange={(e) =>
                          handleStatusChange(m._id, e.target.value as Status)
                        }
                        displayEmpty
                      >
                        <MenuItem value="new">New</MenuItem>
                        <MenuItem value="answered">Answered</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    {new Date(m.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteId(m._id)}
                      aria-label="Delete"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {messages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No messages yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete message?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This message will be permanently deleted. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

